import React from 'react';
import {
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
} from '@mui/icons-material';
import { alpha } from '../../../styles/theme';
import { DroneStatus } from '../../../types/api';
import { Constants } from '../../../types/supabase';

// Status + role presentation config split out of OverviewTab (STD-014). Pure
// data: color/bg/icon/i18n-key lookups keyed by drone status and personnel
// role, plus the maintenance-hours formatter. No React state, so it's directly
// unit-testable.

export interface StatusConfigEntry {
  color: string;
  bgColor: string;
  icon: React.ElementType;
  labelKey: string;
  tooltipKey: string;
}

// Status configuration with colors and icons for all drone statuses
export const STATUS_CONFIG: Record<DroneStatus, StatusConfigEntry> = {
  Airworthy: {
    color: '#4caf50',
    bgColor: alpha('#4caf50', 0.05),
    icon: CheckCircleIcon,
    labelKey: 'droneStatus.airworthy',
    tooltipKey: 'droneStatus.airworthyTooltip',
  },
  'Airworthy with limitations': {
    color: '#FFC72C', // Brand yellow
    bgColor: alpha('#FFC72C', 0.05),
    icon: WarningIcon,
    labelKey: 'droneStatus.airworthyWithLimitations',
    tooltipKey: 'droneStatus.airworthyWithLimitationsTooltip',
  },
  Maintenance: {
    color: '#ff9800',
    bgColor: alpha('#ff9800', 0.05),
    icon: BuildIcon,
    labelKey: 'droneStatus.maintenance',
    tooltipKey: 'droneStatus.maintenanceTooltip',
  },
  'Not Airworthy': {
    color: '#f44336',
    bgColor: alpha('#f44336', 0.05),
    icon: CancelIcon,
    labelKey: 'droneStatus.notAirworthy',
    tooltipKey: 'droneStatus.notAirworthyTooltip',
  },
  'With customer': {
    color: '#2196f3',
    bgColor: alpha('#2196f3', 0.05),
    icon: CheckCircleIcon,
    labelKey: 'droneStatus.withCustomer',
    tooltipKey: 'droneStatus.withCustomerTooltip',
  },
  'In production': {
    color: '#9c27b0', // Purple — pre-service lifecycle state (being built/assembled)
    bgColor: alpha('#9c27b0', 0.05),
    icon: PrecisionManufacturingIcon,
    labelKey: 'droneStatus.inProduction',
    tooltipKey: 'droneStatus.inProductionTooltip',
  },
};

// Dropdown options for drone status. Sourced from the generated Postgres enum
// `public.drone_status` (the single source of truth), so adding a value via a
// migration + `scripts/db-sync.sh` surfaces it in every status dropdown
// automatically. Order follows the enum's sort order.
export const DRONE_STATUS_OPTIONS: readonly DroneStatus[] = Constants.public.Enums.drone_status;

export interface RoleConfigEntry {
  color: string;
  bgColor: string;
}

// Role configuration for personnel cards
export const ROLE_CONFIG: Record<string, RoleConfigEntry> = {
  pilot: {
    color: '#2196f3',
    bgColor: alpha('#2196f3', 0.05),
  },
  maintenance: {
    color: '#ff9800',
    bgColor: alpha('#ff9800', 0.05),
  },
};

// Resolve the config for a drone status, defaulting to Airworthy when the
// status is missing or unknown (matches the previous inline `|| 'Airworthy'`).
export function statusConfigFor(status: DroneStatus | null | undefined): StatusConfigEntry {
  return STATUS_CONFIG[(status as DroneStatus) || 'Airworthy'] ?? STATUS_CONFIG.Airworthy;
}
